import { faCircleInfo, faRotate } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Client from 'fhir-kit-client';
import { Bundle, Device, Parameters, TestPlan, OperationOutcome } from "fhir/r5";
import i18n from "i18next";
import { FunctionComponent, useCallback, useState } from "react";
import { Button, Card, Form, Table } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import PandoraPage from '../../components/PandoraPage/PandoraPage';
import { Title, useToasts } from "@fyrstain/fhir-front-library";
import styles from "./TestPlanSystemsSelection.module.css";
import { deviceDisplayName, deviceTypes } from "../../services/fhirR4CrossVersion";
import { useOnMount } from "src/hooks/useOnMount";

const TestPlanSystemsSelection: FunctionComponent = () => {

    const clientTooltip = i18n.t('tooltip.clientdescription');
    const serverTooltip = i18n.t('tooltip.serverdescription');

    /////////////////////////////////////
    //             State               //
    /////////////////////////////////////

    const [deviceClient, setDeviceClient] = useState("")
    const [deviceServer, setDeviceServer] = useState("");
    const [loading, setLoading] = useState(false);

    const { submitToastsFromOperationOutcome } = useToasts();

    /////////////////////////////////////
    //      Constants / ValueSet       //
    /////////////////////////////////////

    const { testPlanId } = useParams();
    const [testPlanName, setTestPlanName] = useState("");
    const [testPlanDescription, setTestPlanDescription] = useState("");
    //Go with the assumption the TestPlan is always automatable, letting the back handle exceptions.
    const [isAutomatable] = useState(true);
    //Default to true, as we do not currently handle manual TestPlan
    const [isAutomated, setIsAutomated] = useState(true);
    const [devicesClient, setDevicesClient] = useState([] as Device[]);
    const [devicesServer, setDevicesServer] = useState([] as Device[]);
    const [warningMessage, setWarningMessage] = useState("");
    //TODO Check the type of the actor.
    const [clientActor] = useState('N/A');
    const [serverActor] = useState('N/A');

    /////////////////////////////////////
    //             Client              //
    /////////////////////////////////////

    const fhirClient = new Client({
        baseUrl: process.env.REACT_APP_FHIR_URL ?? 'fhir'
    });

    /////////////////////////////////////
    //             Actions             //
    /////////////////////////////////////

    const navigate = useNavigate();

    const toReportDetails = useCallback((id: string) => {
        navigate("/TestPlan/Report/" + id);
    }, [navigate]);

    async function launchTest() {
        startTestPlan();
    }

    useOnMount(() => {
        void loadTestPlanInformations();
        void loadPage();
    });

    const onError = useCallback((error?: unknown) => {
        navigate("/Error", { state: { error } });
    }, [navigate]);

    /**
     * Load the TestPlan informations to display in fields.
     */
    async function loadTestPlanInformations() {
        try {
            const response = await fhirClient.request('/TestPlan/' + testPlanId, { method: 'GET' });
            const testPlan: TestPlan = response as TestPlan;
            setTestPlanName(testPlan.title ?? "");
            setTestPlanDescription(testPlan.description ?? "undefined");
            //TODO check if the test plan is indeed automatable
        } catch (error) {
            console.log(error);
            onError();
        }
        return [];
    }

    /**
     * start the Test script to build the test.
     */
    async function startTestPlan() {
        try {
            const bodyParameters = {
                "resourceType": "Parameters",
                "parameter": [
                    {
                        "name": "serverID",
                        "valueString": deviceServer
                    },
                    {
                        "name": "clientID",
                        "valueString": deviceClient
                    },
                    {
                        "name": "automatic",
                        "valueBoolean": isAutomated
                    }
                ]
            } as Parameters;
            const response = await fhirClient.operation({
                name: '$launchTest',
                resourceType: 'TestPlan',
                id: testPlanId,
                method: 'POST',
                input: bodyParameters,
            });
            const report: TestPlan = response as TestPlan;
            toReportDetails(report.id ?? "");
        } catch (error: any) {
            try {
                if (error.response.data) {
                    console.log(error.response.data)
                    submitToastsFromOperationOutcome(error.response.data as OperationOutcome, 10000);
                } else {
                    console.log(error);
                    onError();
                }
            } catch (error: any) {
                console.log(error);
                onError();
            }
        }
    }

    /**
     * Load the page used for select fields.
     */
    async function loadPage() {
        setLoading(true);
        await loadSystemData();
        await loadTestPlanInformations();
        setLoading(false);
    }

    /**
     * Load the Device name to display in select fields.
     */
    async function loadSystemData() {
        try {
            const response = await fhirClient.search({
                resourceType: 'Device',
                searchParams: {
                    "type": "http://fyrstain.com/pdt/ValueSet/systemTypes|simulator,http://fyrstain.com/pdt/ValueSet/systemTypes|sut"
                }
            })
            const bundle: Bundle = response as Bundle;
            const devices = bundle.entry?.map(
                entry => entry.resource as Device
            ) ?? [];
            setDevicesClient(devices);
            setDevicesServer(devices);
        } catch (error) {
            console.log(error);
            onError();
        }
    }

    function reset() {
        setLoading(true);
        setDeviceClient("undefined");
        setDeviceServer("undefined");
        setWarningMessage("");
        setLoading(false);
    }

    /**
     * Reset the row intended for the client
     */
    function resetClient() {
        setDeviceClient("undefined");
    }

    /**
    * Reset the row intended for the server
    */
    function resetServer() {
        setDeviceServer("undefined");
    }

    /**
     * Check if the device is a simulator.
     * 
     * @param device 
     * 
     */
    function isSimulator(device: Device): boolean {
        return deviceTypes(device).some(type => type.coding?.some((coding: any) => coding.code === "simulator"));
    }

    /**
     * Handle the change of the client select field.
     * 
     * @param event 
     */
    function handleClientChange(event: React.ChangeEvent<HTMLSelectElement>) {
        const selectedDeviceId = event.target.value;
        const selectedDevice = devicesClient.find(device => device.id === selectedDeviceId);

        if (!isAutomatable && selectedDevice && isSimulator(selectedDevice)) {
            setWarningMessage(i18n.t('infomessage.simulator'));
        } else {
            setWarningMessage("");
        }
        setDeviceClient(selectedDeviceId);
    }

    /////////////////////////////////////
    //          Page Content           //
    /////////////////////////////////////

    function getOption(device: Device) {
        return <option value={device.id}>
            {deviceDisplayName(device)} / {deviceTypes(device).map(type => type.coding?.map((coding: any) => coding.display ?? ""))}
        </option>;
    }

    return (
        <PandoraPage titleKey='title.systemsselection' loading={loading} needsLogin={true} >
            <div className={styles.main}>
                <Card className={styles.card}>
                    <Card.Header>
                        <Title level={2} content={i18n.t('title.testinformations')} />
                    </Card.Header>
                    <Card.Body className="cardBody">
                        <div className={styles.form}>
                            <div className={styles.formTextLabel}>
                                <Form.Label className={styles.formLabel}>
                                    <strong className={styles.label}>
                                        ID :
                                    </strong>
                                </Form.Label>
                                <Form.Text>
                                    {testPlanId}
                                </Form.Text>
                            </div>
                            <div className={styles.formTextLabel}>
                                <Form.Label className={styles.formLabel}>
                                    <strong className={styles.label}>
                                        {i18n.t('label.name')} :
                                    </strong>
                                </Form.Label>
                                <Form.Text>
                                    {testPlanName}
                                </Form.Text>
                            </div>
                            <div className={styles.formTextLabel}>
                                <Form.Label className={styles.formLabel}>
                                    <strong className={styles.label}>
                                        {i18n.t('label.generaldescription')} :
                                    </strong>
                                </Form.Label>
                                <Form.Text>
                                    {testPlanDescription}
                                </Form.Text>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Header>
                        <Title level={2} content={i18n.t('title.launchtest')} />
                    </Card.Header>
                    <Card.Body className="cardBody">
                        <Form className='flexWrapStart'>
                            <Form.Check
                                type="switch"
                                id="automated"
                                className={styles.formTextLabel}
                                disabled={true}
                                //TODO For now it is disabled because we can't handle manual TestPlans.
                                //{!isAutomatable}
                                checked={isAutomated}
                                onChange={() => setIsAutomated(!isAutomated)}
                            />
                            <p>
                                {i18n.t('switchbutton.automated')}
                            </p>
                        </Form>
                        <div className={styles.systemsTable}>
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>{i18n.t('title.systems')}</th>
                                        <th>Type</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>
                                            <div className="largeContainer spaceBetweenContainer select">
                                                <Form.Select className={styles.formSelect}
                                                    value={deviceClient}
                                                    onChange={handleClientChange}
                                                >
                                                    <option value="undefined">
                                                        {i18n.t('placeholder.client')}
                                                    </option>
                                                    {devicesClient.map(device => getOption(device))}
                                                </Form.Select>
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary btnInfo"
                                                    data-toggle="tooltip"
                                                    data-placement="right"
                                                    title={clientTooltip}
                                                >
                                                    <FontAwesomeIcon icon={faCircleInfo} />
                                                </button>
                                            </div>
                                        </td>
                                        <td>{clientActor}</td>
                                        <td>
                                            <FontAwesomeIcon
                                                icon={faRotate}
                                                className='actionIcon'
                                                onClick={resetClient}
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div className="largeContainer spaceBetweenContainer select">
                                                <Form.Select className={styles.formSelect}
                                                    value={deviceServer}
                                                    onChange={(e) => {
                                                        setDeviceServer(e.target.value);
                                                    }}
                                                >
                                                    <option value="undefined">
                                                        {i18n.t('placeholder.server')}
                                                    </option>
                                                    {devicesServer.map(device => getOption(device))}
                                                </Form.Select>
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary btnInfo"
                                                    data-toggle="tooltip"
                                                    data-placement="right"
                                                    title={serverTooltip}
                                                >
                                                    <FontAwesomeIcon icon={faCircleInfo} />
                                                </button>
                                            </div>
                                        </td>
                                        <td>{serverActor}</td>
                                        <td>
                                            <FontAwesomeIcon
                                                icon={faRotate}
                                                className='actionIcon'
                                                onClick={resetServer}
                                            />
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </div>
                        {warningMessage && (
                            <div
                                className={["alert alert-warning", styles.warningAlert].join(' ')}
                                role="alert"
                            >
                                {warningMessage}
                            </div>
                        )}
                        <div className={styles.btnContainer}>
                            <Button
                                className="button"
                                variant="danger"
                                onClick={launchTest}
                            >
                                {i18n.t('button.launch')}
                            </Button>
                            <Button
                                className="button"
                                variant="secondary"
                                onClick={reset}
                            >
                                {i18n.t('button.reset')}
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        </PandoraPage>
    );
};

export default TestPlanSystemsSelection;
